/**
 * Request Instrumentation
 *
 * Dev-only logging, abort reason classification, network failure logging,
 * and Sentry integration for request debugging.
 */

import { withScope, captureMessage, captureException } from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import { apiLogger } from '@/core/utils/logger';
import { getBootId, isEarlyBoot, getTimeSinceBoot } from '@/core/utils/bootSession';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getSentryUserContext } from '@/shared/utils/sentryContext';
import { extractErrorMessage } from '@/shared/utils/error';

/** Window augmentation for dev-only debug properties. */
interface WindowWithDebug extends Window {
  __rtkRequestLog?: Array<RequestInstrumentation & { timestamp: string }>;
}

// DEV-ONLY INSTRUMENTATION FOR DEBUGGING FIRST-LOAD FAILURES

/**
 * Dev-only instrumentation interface for detailed request logging.
 * Helps diagnose issues where first attempt fails but retry succeeds.
 */
export interface RequestInstrumentation {
  endpoint: string;
  fullUrl: string;
  method: string;
  headerKeys: string[];
  credentialsMode: string;
  abortReason?: string;
  durationMs: number;
  retryCount: number;
  requestId: string;
  bootId: string;
  timeSinceBoot: number;
  isEarlyBoot: boolean;
  status?: number | string;
  error?: string;
}

/**
 * Dev-only: Log detailed request instrumentation for debugging.
 * Only logs in development mode to avoid production noise.
 */
export function logRequestInstrumentation(data: RequestInstrumentation): void {
  if (process.env.NODE_ENV !== 'development') return;
  if (typeof window === 'undefined') return;

  const isError = data.status !== undefined && (
    typeof data.status === 'string' ||
    (typeof data.status === 'number' && data.status >= 400)
  );

  const style = isError
    ? 'background: #f44; color: white; padding: 2px 6px; font-weight: bold;'
    : 'background: #333; color: #0f0; padding: 2px 6px;';

  apiLogger.debug(
    `%c[RTK_DEBUG] ${data.method} ${data.endpoint}`,
    style,
    {
      fullUrl: data.fullUrl,
      headerKeys: data.headerKeys,
      credentialsMode: data.credentialsMode,
      durationMs: data.durationMs,
      retryCount: data.retryCount,
      requestId: data.requestId,
      bootId: data.bootId,
      timeSinceBoot: data.timeSinceBoot,
      isEarlyBoot: data.isEarlyBoot,
      status: data.status,
      abortReason: data.abortReason,
      error: data.error,
    }
  );

  // Also store in window for inspection
  const w = window as WindowWithDebug;
  if (!w.__rtkRequestLog) {
    w.__rtkRequestLog = [];
  }
  w.__rtkRequestLog.push({
    ...data,
    timestamp: new Date().toISOString(),
  });
  // Keep only last 50 entries
  if (w.__rtkRequestLog.length > 50) {
    w.__rtkRequestLog.shift();
  }
}

// REQUEST ID GENERATION

/**
 * Generate a unique request ID for correlation with server logs.
 * Uses crypto.randomUUID() if available, falls back to a simple implementation.
 */
export function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// NETWORK FAILURE LOGGING + SENTRY

/**
 * Network failure log entry interface for structured logging.
 */
export interface NetworkFailureLogEntry {
  requestId: string;
  endpoint: string;
  url: string;
  method: string;
  status: number | string;
  durationMs: number;
  abortReason?: string;
  retryCount: number;
  errorMessage?: string;
  timestamp: string;
}

/**
 * Abort reason classification for better error attribution.
 * Distinguishes between:
 * - 'timeout': Request exceeded configured timeout
 * - 'navigation': User navigated away (route change)
 * - 'unmount': Component unmounted (React cleanup)
 * - 'manual': Explicit AbortController.abort() call
 * - 'network_error': Network connectivity issue
 * - 'fetch_failed': Fetch API failure — typically a 502/503 from the reverse proxy
 *   (Railway) during cold-start, returned without CORS headers so the browser
 *   reports it as "CORS policy blocked". NOT an actual CORS misconfiguration.
 */
export type AbortReason =
  | 'timeout'
  | 'navigation'
  | 'unmount'
  | 'manual'
  | 'network_error'
  | 'fetch_failed'
  | 'fetch_error'
  | 'unknown';

/**
 * Extract and classify abort reason from error object.
 * Provides granular classification for better debugging and error attribution.
 *
 * Key distinction: "timeout" means the request genuinely took too long,
 * while "navigation"/"unmount" means the user/app cancelled it (not a real failure).
 */
export function extractAbortReason(error: FetchBaseQueryError & { error?: string }): AbortReason | undefined {
  const errorString = String(error.error || '').toLowerCase();

  // 1. Timeout: Request exceeded configured timeout limit
  // This is a REAL failure that should be tracked
  if (errorString.includes('timeouterror') || errorString.includes('timed out') || errorString.includes('timeout')) {
    return 'timeout';
  }

  // 2. Abort scenarios - need to distinguish cause
  if (errorString.includes('aborterror') || errorString.includes('aborted') || errorString.includes('abort')) {
    // Check for navigation-related abort signals
    // RTK Query uses "Aborted due to condition" for skip changes
    if (errorString.includes('condition') || errorString.includes('skip')) {
      return 'unmount';
    }
    // Check for user-initiated navigation hints
    if (errorString.includes('navigation') || errorString.includes('navigate')) {
      return 'navigation';
    }
    // Generic abort - could be manual or from component cleanup
    // In production, this is usually component unmount during navigation
    return 'manual';
  }

  // 3. Network/fetch errors
  if (error.status === 'FETCH_ERROR') {
    if (errorString.includes('network') || errorString.includes('offline') || errorString.includes('internet')) {
      return 'network_error';
    }
    // "Failed to fetch" / "Load failed" = browser got no readable HTTP response.
    // Most common cause: Railway proxy returned 502/503 without CORS headers
    // during backend cold-start. The browser surfaces this as a CORS error,
    // but the root cause is the upstream being temporarily unavailable.
    if (errorString.includes('failed to fetch') || errorString.includes('load failed')) {
      return 'fetch_failed';
    }
    // Check for CORS hints
    if (errorString.includes('cors') || errorString.includes('cross-origin')) {
      return 'fetch_error';
    }
    return 'fetch_error';
  }

  return undefined;
}

/**
 * Check if an abort reason represents a "real" failure vs user-initiated cancellation.
 * Real failures should be logged to Sentry; cancellations should be silent.
 */
export function isRealFailure(reason: AbortReason | undefined): boolean {
  if (!reason) return true; // Unknown = assume real
  // Navigation and unmount are NOT real failures - the user/app intentionally cancelled
  return !['navigation', 'unmount', 'manual'].includes(reason);
}

/**
 * Determine if an error should be captured to Sentry.
 * We only capture meaningful failures to avoid spam:
 * - Timeouts (real request timeout, not navigation abort)
 * - Fetch/network errors
 * - 5xx server errors
 * - Early boot failures (first 30 seconds)
 *
 * We SKIP:
 * - Navigation/unmount aborts (user clicked away, component unmounted)
 * - 4xx client errors (expected application errors)
 */
function shouldCaptureToSentry(entry: NetworkFailureLogEntry): boolean {
  const status = entry.status;
  const abortReason = entry.abortReason as AbortReason | undefined;

  // SKIP navigation/unmount aborts - these are NOT real failures
  // The user/app intentionally cancelled the request
  if (abortReason && !isRealFailure(abortReason)) {
    return false;
  }

  // SKIP transient failures after a successful token refresh.
  // Flow: 401 → refresh succeeded → retry got FETCH_ERROR (transient network blip).
  // Session is preserved, user can retry manually — not actionable.
  if (entry.abortReason === 'retry_after_refresh_failed' && (status === 'FETCH_ERROR' || status === 0)) {
    return false;
  }

  // SKIP 401s — always handled by auth flow (refresh → redirect to /login).
  // These are never actionable Sentry alerts, even during early boot.
  if (status === 401) {
    return false;
  }

  // Always capture early boot failures - these indicate startup problems
  if (isEarlyBoot()) {
    return true;
  }

  // Capture genuine timeouts (request took too long)
  if (abortReason === 'timeout') {
    return true;
  }

  // Capture network/fetch errors (connectivity issues)
  if (status === 'FETCH_ERROR' || status === 0 || abortReason === 'network_error' || abortReason === 'fetch_failed') {
    return true;
  }

  // Capture fetch errors that aren't user-initiated
  if (abortReason === 'fetch_error') {
    return true;
  }

  // Capture 5xx server errors
  if (typeof status === 'number' && status >= 500 && status < 600) {
    return true;
  }

  // Skip 4xx client errors (400, 401, 403, 404, 429) - these are expected
  // Skip successful retries (they'll be captured if they ultimately fail)
  return false;
}

/**
 * Get Sentry severity level based on error type.
 */
function getSentrySeverity(entry: NetworkFailureLogEntry): SeverityLevel {
  const status = entry.status;

  // 5xx errors are more severe
  if (typeof status === 'number' && status >= 500) {
    return 'error';
  }

  // Early boot failures are warnings - they might recover
  if (isEarlyBoot()) {
    return 'warning';
  }

  // Timeouts and network errors are warnings
  return 'warning';
}

/**
 * Log network failure with comprehensive details for debugging and server correlation.
 * Also captures to Sentry with proper tags for filtering.
 */
export function logNetworkFailure(entry: NetworkFailureLogEntry): void {
  // Console logging for local debugging
  apiLogger.warn('[Network Failure]', {
    requestId: entry.requestId,
    endpoint: entry.endpoint,
    url: entry.url,
    method: entry.method,
    status: entry.status,
    durationMs: entry.durationMs,
    abortReason: entry.abortReason,
    retryCount: entry.retryCount,
    errorMessage: entry.errorMessage,
    timestamp: entry.timestamp,
  });

  // Capture to Sentry if this is a meaningful failure
  if (shouldCaptureToSentry(entry)) {
    const severity = getSentrySeverity(entry);

    withScope((scope) => {
      // Tags for filtering in Sentry UI
      scope.setTag('rtk.endpoint', entry.endpoint);
      scope.setTag('http.method', entry.method);
      scope.setTag('http.status_code', String(entry.status));
      scope.setTag('rtk.retry_count', String(entry.retryCount));
      scope.setTag('boot_id', getBootId());

      // User/org context for filtering (opaque IDs only — no PII)
      const userCtx = getSentryUserContext();
      if (userCtx.userId) scope.setTag('user_id', userCtx.userId);
      if (userCtx.organizationId) scope.setTag('organization_id', userCtx.organizationId);

      // Extra context for debugging
      scope.setExtra('url', entry.url);
      scope.setExtra('duration_ms', entry.durationMs);
      scope.setExtra('abort_reason', entry.abortReason);
      scope.setExtra('request_id', entry.requestId);
      scope.setExtra('error_message', entry.errorMessage);
      scope.setExtra('timestamp', entry.timestamp);
      scope.setExtra('time_since_boot_ms', getTimeSinceBoot());
      scope.setExtra('is_early_boot', isEarlyBoot());

      // Set fingerprint to group similar errors
      scope.setFingerprint([
        'rtkq-failure',
        entry.endpoint,
        String(entry.status),
        entry.abortReason || 'unknown',
      ]);

      // Capture as message with appropriate severity
      const message = `RTKQ request failed: ${entry.endpoint} (${entry.status})`;
      captureMessage(message, severity);
    });
  }
}

/**
 * Capture unexpected response shape errors as Sentry exceptions.
 * Use this for errors that indicate a bug or unexpected server behavior,
 * not for expected error codes like 401/403/404.
 */
export function captureUnexpectedResponseError(
  error: Error | unknown,
  context: {
    endpoint: string;
    url: string;
    method: string;
    requestId: string;
    expectedShape?: string;
    actualResponse?: unknown;
  }
): void {
  withScope((scope) => {
    // Tags for filtering
    scope.setTag('rtk.endpoint', context.endpoint);
    scope.setTag('http.method', context.method);
    scope.setTag('error.type', 'unexpected_response_shape');
    scope.setTag('boot_id', getBootId());

    // User/org context for filtering (opaque IDs only — no PII)
    const userCtx = getSentryUserContext();
    if (userCtx.userId) scope.setTag('user_id', userCtx.userId);
    if (userCtx.organizationId) scope.setTag('organization_id', userCtx.organizationId);

    // Extra context
    scope.setExtra('url', context.url);
    scope.setExtra('request_id', context.requestId);
    scope.setExtra('expected_shape', context.expectedShape);
    scope.setExtra('actual_response', context.actualResponse);
    scope.setExtra('time_since_boot_ms', getTimeSinceBoot());
    scope.setExtra('is_early_boot', isEarlyBoot());

    // Set fingerprint
    scope.setFingerprint([
      'rtkq-unexpected-response',
      context.endpoint,
      context.expectedShape || 'unknown',
    ]);

    // Capture as exception (error level)
    if (error instanceof Error) {
      captureException(error);
    } else {
      captureException(new Error(`Unexpected response shape from ${context.endpoint}: ${extractErrorMessage(error)}`));
    }
  });
}
